import React, { useContext, useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import BotStatusContext from '../../../context/BotStatusContext/BotStatusContext';
import LiveBotVersion from '../../../types/LiveBotVersion';

interface BotStatusConfig {
  status: string;
  text: string;
  outerColor: string;
  innerColor: string;
}

function BotStatus(): React.ReactElement | null {
  const { currentChatbot } = useContext(ChatbotContext);
  const { liveBotVersion: contextLiveBotVersion, isDraftVersion } =
    useContext(BotStatusContext);

  const [displayedBotVersion, setDisplayedBotVersion] =
    useState<LiveBotVersion | null>(
      contextLiveBotVersion || currentChatbot?.liveVersions?.[0] || null
    );

  useEffect(() => {
    if (
      currentChatbot?.liveVersions?.[0] &&
      !contextLiveBotVersion &&
      !displayedBotVersion
    ) {
      setDisplayedBotVersion(currentChatbot.liveVersions[0]);
    }
  }, [
    currentChatbot?.liveVersions,
    contextLiveBotVersion,
    displayedBotVersion
  ]);

  useEffect(() => {
    if (contextLiveBotVersion) {
      setDisplayedBotVersion(contextLiveBotVersion);
    }
  }, [contextLiveBotVersion]);

  const getFormattedDate = (botVersion: LiveBotVersion): string | null => {
    const { creationTime } = botVersion;
    const date = new Date(creationTime);
    const formattedDate = date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    if (formattedDate !== 'Invalid Date') {
      return formattedDate;
    }
    return null;
  };

  const getFormattedTime = (botVersion: LiveBotVersion): string | null => {
    const { creationTime } = botVersion;
    const date = new Date(creationTime);
    const formattedTime = date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return formattedTime;
  };

  const getStatusConfig = (buildState?: string): BotStatusConfig | null => {
    if (!buildState || isDraftVersion) {
      return {
        status: 'offline',
        text: 'offline',
        outerColor: 'lightgray',
        innerColor: 'gray'
      };
    }

    if (
      buildState === 'BUILDING' ||
      buildState === 'PARTIALLY_READY' ||
      buildState === 'NOT_BUILT'
    ) {
      return {
        status: 'building',
        text: 'building',
        outerColor: '#FFBF00',
        innerColor: '#FFE599'
      };
    }

    if (buildState === 'READY') {
      return {
        status: 'live',
        text: 'live',
        outerColor: '#037971',
        innerColor: '#07EDDC'
      };
    }

    if (buildState === 'BUILD_FAILED' || buildState === 'DEACTIVATED') {
      return {
        status: 'offline',
        text: 'offline',
        outerColor: '#FF9999',
        innerColor: '#FF4C4C'
      };
    }

    return null;
  };

  let statusConfig = null;
  if (isDraftVersion) {
    statusConfig = getStatusConfig();
  } else if (displayedBotVersion) {
    statusConfig = getStatusConfig(displayedBotVersion.buildState);
  }

  if (!statusConfig) return <div className='h-6' />;

  const formattedDate = displayedBotVersion
    ? getFormattedDate(displayedBotVersion)
    : null;
  const formattedTime = displayedBotVersion
    ? getFormattedTime(displayedBotVersion)
    : null;

  return (
    <div className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 md:gap-8'>
      <div className='flex items-center space-x-2'>
        <div className='flex items-center'>
          <div
            className='h-2.5 w-2.5 mr-3 md:mr-2 rounded-full shadow-[0px_0px_1px_1px_rgba(0,0,0,0.1)]'
            style={
              {
                backgroundColor: statusConfig.innerColor,
                animation:
                  statusConfig.status === 'building' ||
                  statusConfig.status === 'live'
                    ? `pulse-animation-${statusConfig.status} 2s infinite`
                    : 'none',
                '--pulse-color': statusConfig.outerColor
              } as React.CSSProperties
            }
          />
          <span className='font-medium capitalize text-base text-white whitespace-nowrap'>
            {statusConfig.text}
          </span>
        </div>
        {formattedDate && (
          <span className='text-sm text-white/80 whitespace-nowrap'>
            Last updated: {formattedDate}
            <span className='hidden xs:inline'> {formattedTime}</span>
          </span>
        )}
      </div>
      {displayedBotVersion && !isDraftVersion && (
        <a
          href={`${process.env.REACT_APP_BASE_URL}/public/chatbot-view/${currentChatbot?.uuid}`}
          target='_blank'
          rel='noopener noreferrer'
          className='flex items-center text-white hover:text-white/80 transition-colors whitespace-nowrap ml-0 sm:ml-auto'
        >
          <span className='text-base'>View your chatbot</span>
          <ExternalLink className='ml-2 h-4 w-4 flex-shrink-0' />
        </a>
      )}
    </div>
  );
}

export default BotStatus;
